import { useMemo } from 'react';
import isEqual from 'lodash.isequal';
import { toast } from 'react-toastify';
import FormSmallModal, { InputRow } from '../global/FormSmallModal';
import useBusiness from '../../hooks/businesses/useBusiness';
import useUser from '../../hooks/global/useUser';
import { getBusinessCurrency } from '../../helpers/businesses';
import {
  giftCardSettingsSchema,
  GiftCardSettingsPayload,
} from '../../types/gift-cards';
import useUpdateGiftCardSettings from '../../hooks/giftCards/useUpdateGiftCardSettings';
import useGetGiftCardItem from '../../hooks/giftCards/useGetGiftCardItem';
import { canManageGiftCards } from '../../helpers/gift-cards';
import { invalidateSearchItems } from '../../services/items';
import useGetGiftCardsOverview from '../../hooks/giftCards/useGetGiftCardsOverview';
import GiftCardStoreShare from './GiftCardStoreShare';

const EXPIRATION_YEARS = 10;
const DAYS_PER_YEAR = 365;

const EXPIRATION_OPTIONS = [
  { value: '', label: 'No expiration' },
  ...Array.from({ length: EXPIRATION_YEARS }, (_, i) => {
    const years = i + 1;
    const days = years * DAYS_PER_YEAR;
    return {
      value: days,
      label: years === 1 ? '1 year' : `${years} years`,
    };
  }),
];

interface Props {
  open: boolean;
  onClose: () => void;
}

function GiftCardSettingsModal({ open, onClose }: Props) {
  const { user } = useUser();
  const { business } = useBusiness();
  const { data: giftCardItem, isLoading: isLoadingGiftCardItem } =
    useGetGiftCardItem({
      enabled: open,
    });
  const { data: overview, refetch: refetchOverview } =
    useGetGiftCardsOverview();
  const { mutateAsync: updateGiftCardSettings } = useUpdateGiftCardSettings({
    onSuccess: () => {
      invalidateSearchItems();
      refetchOverview();
      toast.success('Gift card settings updated!');
    },
    onError: error => {
      toast.error(error);
    },
  });

  const isEditingAllowed = canManageGiftCards(user);
  const currency = getBusinessCurrency(business);

  const initialValues = useMemo(
    () => ({
      enabled: giftCardItem ? !giftCardItem.deletedAt : false,
      defaultGiftCardBalance: giftCardItem?.price ?? 0,
      expirationDays: overview?.expirationDays || '',
    }),
    [giftCardItem, overview?.expirationDays],
  );

  const inputRows = useMemo(() => {
    const tempInputRows: InputRow<GiftCardSettingsPayload>[] = [
      [
        {
          name: 'enabled',
          inputType: 'switch',
          label: 'Enable gift card sales',
          tooltip:
            'Enable or disable gift card sales for your business. When disabled, existing gift cards will still be redeemable.',
          disabled: isLoadingGiftCardItem,
        },
      ],
      [
        {
          name: 'defaultGiftCardBalance',
          inputType: 'number',
          label: 'Default gift card balance',
          tooltip:
            'The default purchase amount and balance for new gift cards. This can be changed during checkout for each purchase.',
          placeholder: 'Enter amount',
          min: 0,
          suffix: currency,
          disabled: isLoadingGiftCardItem,
        },
      ],
      [
        {
          name: 'expirationDays',
          inputType: 'select',
          label: 'Gift card expiration',
          tooltip:
            'How long after purchase gift cards are valid. No expiration means gift cards never expire. This will only apply to new gift cards. Existing gift cards will not be affected.',
          placeholder: 'Select expiration',
          options: EXPIRATION_OPTIONS,
          disabled: isLoadingGiftCardItem,
        },
      ],
    ];

    return tempInputRows;
  }, [currency, isLoadingGiftCardItem]);

  const shopUrl = business?.urlPath;

  return (
    <FormSmallModal
      open={open}
      onClose={onClose}
      allowUntouched={false}
      form={{
        title: 'Gift Card Settings',
        initialValues,
        inputRows,
        onSubmit: async values => {
          if (!isEditingAllowed || isLoadingGiftCardItem) return;
          const isChanged = !isEqual(values, initialValues);
          if (!isChanged) return;

          await updateGiftCardSettings({
            enabled: values.enabled,
            defaultGiftCardBalance: values.defaultGiftCardBalance,
            expirationDays: values.expirationDays
              ? Number(values.expirationDays)
              : null,
          });

          onClose();
        },
        validationSchema: giftCardSettingsSchema,
        submitButtonText: 'Save',
      }}>
      {!isLoadingGiftCardItem && shopUrl && (
        <GiftCardStoreShare shopUrl={shopUrl} />
      )}
    </FormSmallModal>
  );
}

export default GiftCardSettingsModal;
